import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Cog8ToothIcon } from "@heroicons/react/24/solid";
import {
	ChevronDownIcon,
	PencilIcon,
	XCircleIcon,
	DocumentIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { AlertLevel } from "../../components/AlertMessage";
import NoDataTableBody from "../../components/NoDataTableBody";
import FormattedDate from "../../components/FormattedDate";
import Avatar from "../../components/Avatar";
import ModalForm from "../../components/ModalForm";
import ModalAlert from "../../components/ModalAlert";
import TextWithTip from "../../components/TextWithTip";
import { useRemoveAvatarMutation } from "../account/accountApiSlice";
import { ProctorUserModel, useEditStudentMutation } from "../users/usersApiSlice";
import EditStudentForm from "../users/EditStudentForm";
import {
	CourseWriterRow,
	useRemoveStudentsMutation,
	ManageCourseWritersViewModel,
} from "./manageCourseApiSlice";

interface Props {
	rosterData: ManageCourseWritersViewModel;
	setRemoveStudentMessage?: (message: string) => void;
	setErrorMessage?: (message: string) => void;
}

export default function CourseRoster({
	rosterData,
	setRemoveStudentMessage,
	setErrorMessage,
}: Props) {
	return (
		<table className="table-fixed tbl tbl-data mt-2">
			<thead>
				<tr>
					<th className="hidden">ID</th>
					<th className="w-1/12 print:hidden"></th>
					<th className="w-2/12">First Name</th>
					<th className="w-2/12">Last Name</th>
					<th className="w-4/12">Username</th>
					<th className="w-2/12">Last Login</th>
					<th className="w-1/12 print:hidden"></th>
				</tr>
			</thead>
			{!rosterData ? (
				<tbody>
					<tr className="loading">
						<td colSpan={999}>Data loading...</td>
					</tr>
				</tbody>
			) : rosterData.writersPage.items.length === 0 ? (
				<NoDataTableBody
					columnCount={7}
					message="There are no students registered in this course meeting the criteria."
				/>
			) : (
				<tbody>
					{rosterData.writersPage.items.map((row) => {
						return (
							<tr key={row.id} className="print:break-inside-avoid-page">
								<StudentRow
									row={row}
									allowChanges={rosterData.allowChanges}
									setRemoveMessage={setRemoveStudentMessage}
									setErrorMessage={setErrorMessage}
								/>
							</tr>
						);
					})}
				</tbody>
			)}
		</table>
	);
}

interface StudentRowProps {
	row: CourseWriterRow;
	allowChanges: boolean;
	setRemoveMessage?: (message: string) => void;
	setErrorMessage?: (message: string) => void;
}

function StudentRow({ row, allowChanges, setRemoveMessage, setErrorMessage }: StudentRowProps) {
	const navigate = useNavigate();
	const { course } = useAppSelector((state) => state.user);

	const [editModel, setEditModel] = useState<ProctorUserModel>();
	const [disableEditStudentConfirm, setDisableEditStudentConfirm] = useState(false);
	const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
	const [openEditStudentDialog, setOpenEditStudentDialog] = useState(false);

	const [editStudent] = useEditStudentMutation();
	const [removeStudent] = useRemoveStudentsMutation();
	const [removeUserImage] = useRemoveAvatarMutation();

	function handleEditStudent(model?: ProctorUserModel) {
		if (model) {
			setOpenEditStudentDialog(false);
			editStudent(model)
				.unwrap()
				.then(() => {
					if (setErrorMessage) {
						setErrorMessage("");
					}
					if (!model.allowUserImages && model.hasImage) {
						removeUserImage({ userId: row.id });
					}
				})
				.catch((error) => {
					if (setErrorMessage) {
						setErrorMessage(`Student could not be edited. ${error.data}`);
					}
				});
		}
	}

	function handleRemoveStudent(id: number) {
		if (id) {
			removeStudent(id)
				.unwrap()
				.then((payload) => {
					if (setErrorMessage) {
						setErrorMessage("");
					}
					setOpenRemoveDialog(false);
					scrollTo(0, 0);
					if (setRemoveMessage) {
						setRemoveMessage(
							`Removed student ${payload.fullName} (${payload.username}) from the course.`
						);
					}
				})
				.catch((error) => {
					if (setErrorMessage) {
						setErrorMessage(`Student could not be removed from course. ${error.data}`);
					}
				});
		}
	}

	return (
		<>
			<td className="hidden">{row.id}</td>
			<td className="print:hidden">
				<Avatar
					initials={row.initials}
					className="!pr-0.5"
					hasImage={row.hasImage}
					userId={row.id}
				/>
			</td>
			<td>
				<TextWithTip uniqueName={`fName${row.id}`} text={row.fName} />
			</td>
			<td>
				<TextWithTip uniqueName={`lName${row.id}`} text={row.lName} />
			</td>
			<td>
				<TextWithTip uniqueName={`username${row.id}`} text={row.userName} />
			</td>
			<td>{row.lastLoginDate ? <FormattedDate date={row.lastLoginDate} /> : "Never"}</td>
			<td className="print:hidden">
				<Menu as="div" className="flex justify-end">
					<MenuButton id={`settings-button-${row.id}`}>
						<div className="flex items-center justify-center cursor-pointer">
							<Cog8ToothIcon className="size-5" />
							<ChevronDownIcon className="size-4" />
						</div>
					</MenuButton>
					<Transition
						as={Fragment}
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95">
						<MenuItems className="absolute z-10 mt-6 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
							<div id={`edit-${row.id}-button`}>
								<MenuItem key={`edit-${row.id}`}>
									<button
										className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:bg-white disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer"
										onClick={() => setOpenEditStudentDialog(true)}>
										<PencilIcon className="size-4" />
										<span className="ml-2">Edit</span>
									</button>
								</MenuItem>
							</div>
							<div id={`view-drafts-${row.id}-button`}>
								<MenuItem key={`view-drafts-${row.id}`}>
									<button
										className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:bg-white disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer"
										onClick={() =>
											navigate(
												`/writing/viewDrafts?writerId=${row.id}&courseId=${course?.id}`
											)
										}>
										<DocumentIcon className="size-4" />
										<span className="ml-2">Draft History</span>
									</button>
								</MenuItem>
							</div>
							<div id={`remove-${row.id}-button`}>
								<MenuItem key={`remove-${row.id}`}>
									<button
										className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:bg-white disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer"
										onClick={() => setOpenRemoveDialog(true)}
										disabled={!allowChanges}>
										<XCircleIcon className="size-4" />
										<span className="ml-2">Remove</span>
									</button>
								</MenuItem>
							</div>
						</MenuItems>
					</Transition>
				</Menu>
				<ModalForm
					open={openEditStudentDialog}
					title="Edit Student"
					form={
						<EditStudentForm
							studentId={row.id}
							updateStudentModel={setEditModel}
							disableConfirm={setDisableEditStudentConfirm}
						/>
					}
					confirmLabel="Save"
					cancelLabel="Cancel"
					disableConfirm={disableEditStudentConfirm}
					onConfirm={() => handleEditStudent(editModel)}
					onCancel={() => setOpenEditStudentDialog(false)}
				/>
				<ModalAlert
					open={openRemoveDialog}
					title={"Are You Sure?"}
					message={`Are you sure you want to remove ${row.fullName} (${row.userName}) from the course?`}
					type={AlertLevel.Warning}
					confirmLabel="Yes"
					cancelLabel="No"
					onConfirm={() => handleRemoveStudent(row.id)}
					onCancel={() => setOpenRemoveDialog(false)}
				/>
			</td>
		</>
	);
}
